import { ApolloClient, MutationFunction } from '@apollo/client';
import { ChildProps, graphql } from '@apollo/client/react/hoc';
import compose from 'lodash.flowright';
import { clone, path, remove } from 'ramda';
import { Component, ReactNode, createContext } from 'react';
import { AnonymityLevel, GetMessagesQuery, QuestionType } from '../generated/hooks';
import { sendScaleAnswerMutation, sendSelectAnswerMutation, sendTextAnswerMutation } from '../graphql/mutations';
import { messagesQuery } from '../graphql/queries';
import { definitionTypes } from '../lib/common';
import { organizationRespondent } from '../types';
import { CHATTING_ANIMATION_LENGTH } from './constants';
import { isAwaitingLanguageSelection } from './isAwaitingLanguageSelection';

interface IProviderState {
  message: string;
  index: number;
  remainingQuestions: number | null;
  values: number[];
  bounce: boolean;
  typing: boolean;
  err: any;
  typingStart: number;
  hideControls: boolean;
  lastMessage: NonNullable<GetMessagesQuery['messages']>[0] | null;
  inputInitValue: string;
  notDeliveredMessages: any[];
  selectedPersons: organizationRespondent[];
  lastKey: string | undefined;
  // Scale question state (independent from select choices)
  scaleSelections: (number | null)[];
  scaleComments: (string | null)[];
  prefilledScaleSelections: (number | null)[] | null;
  isSending: boolean;
}

interface IProps {
  channel: any;
  startingMessages: any;
  client: any;
  sendSelectAnswer: () => void;
  children: ReactNode;
}

interface ITextMutationPayload {
  sendTextAnswer: {
    id: string;
    text: string;
    createdAt: string;
    createdBy: string;
    anonymityLevel: AnonymityLevel;
    isLast: boolean;
    __typename: string;
  };
}

interface ISelectMutationPayload {
  sendSelectAnswer: {
    id: string;
    text: string;
    createdAt: string;
    createdBy: string;
    anonymityLevel: AnonymityLevel;
    isLast: boolean;
    __typename: string;
  };
}

interface ISendTextMutationInput {
  channelId: string;
  text: string;
}

interface ISendChoiceMutationInput {
  channelId: string;
  value: number[];
}

interface IScaleAnswerInputItem {
  statementIndex: number;
  value: number | null;
  comment: string | null;
}

interface ISendScaleMutationInput {
  channelId: string;
  answers: IScaleAnswerInputItem[];
  previousQuestionId: string | undefined;
}

interface IScaleMutationPayload {
  sendScaleAnswer: {
    id: string;
    text: string;
    createdAt: string;
    createdBy: string;
    anonymityLevel: AnonymityLevel;
    isLast: boolean;
    __typename: string;
    remainingQuestions?: number | null;
    statementIndex?: number;
    comment?: string | null;
    value?: number[];
    response?: NonNullable<GetMessagesQuery['messages']>;
  };
}

interface IMutation {
  sendTextAnswer: MutationFunction<ITextMutationPayload, ISendTextMutationInput>;
  sendSelectAnswer: MutationFunction<ISelectMutationPayload, ISendChoiceMutationInput>;
  sendScaleAnswer: MutationFunction<IScaleMutationPayload, ISendScaleMutationInput>;
}

/* tslint:disable */

export interface IInputContextValue {
  state: IProviderState;
  organizationId?: string;
  changeMessage: (msg: string) => void;
  removeChoice: (index: number) => void;
  sendMessage: (message: string | null, index?: number) => void;
  addChoice: (message: string, values: number) => void;
  rangeChoosed: (message: string, index: number) => void;
  changeLastMessage: (message: any) => void;
  makeBounce: (makeBounce?: boolean) => void;
  editInputValue: (type: string, message: string, values?: number[]) => void;
  handleSetMessage: (message: string) => void;
  handleSelectPerson: (respondent: organizationRespondent) => void;
  handleRemoveRespondent: (respondent: organizationRespondent) => void;
  setLastKey: (key: string | undefined) => void;
  setClearInputFunction: (clearInput: () => void) => void;
  clearInput: () => void;
  setRemainingQuestions: (remainingQuestions: number) => void;
  // scale-specific API (separate from select choices)
  setScaleSelection: (statementIndex: number, choiceIndex: number) => void;
  setScaleComment: (statementIndex: number, comment: string | null) => void;
  initScaleState: (statementsLength: number) => void;
  prefillScaleState: (selections: (number | null)[], comments: (string | null)[]) => void;
  submitScaleAnswers: () => Promise<boolean>;
}

const defaultContextValue: IInputContextValue = {
  state: {
    index: 0,
    values: [],
    message: '',
    bounce: false,
    lastMessage: null,
    notDeliveredMessages: [],
    remainingQuestions: null,
    inputInitValue: '',
    hideControls: false,
    selectedPersons: [],
    typing: false,
    typingStart: 0,
    err: null,
    lastKey: undefined,
    scaleSelections: [],
    scaleComments: [],
    prefilledScaleSelections: null,
    isSending: false,
  },

  /* eslint-disable */
  changeMessage: (message: string) => {},
  removeChoice: (index: number) => {},
  sendMessage: (message: string | null, index?: number) => {},
  addChoice: (message: string, index: number) => {},
  rangeChoosed: (message: string, index: number) => {},
  changeLastMessage: (message: any) => {},
  makeBounce: (makeBounce?: boolean) => {},
  editInputValue: (type: string, message: string) => {},
  handleSetMessage: (message: string) => {},
  handleSelectPerson: (respondent: organizationRespondent) => {},
  handleRemoveRespondent: (respondent: organizationRespondent) => {},
  setLastKey: (key: string | undefined) => {},
  setClearInputFunction: (clearInput: () => void) => {},
  clearInput: () => {},
  setRemainingQuestions: () => {},
  // scale-specific
  setScaleSelection: (statementIndex: number, choiceIndex: number) => {},
  setScaleComment: (statementIndex: number, comment: string | null) => {},
  initScaleState: (statementsLength: number) => {},
  prefillScaleState: (selections: (number | null)[], comments: (string | null)[]) => {},
  submitScaleAnswers: () => Promise.resolve(false),
  /* eslint-enable */
};

/* tslint:enable */

const InputContext = createContext<IInputContextValue>(defaultContextValue);

class InputProviderComponent extends Component<
  ChildProps<IProps & IMutation, ITextMutationPayload & ISelectMutationPayload & IScaleMutationPayload>,
  IProviderState
> {
  state = defaultContextValue.state;

  bounce: any = null;
  clearInputFunction?: () => void;
  private startingMessagesStarted = false;

  componentDidUpdate(prevProps: IProps) {
    if (prevProps.channel?.id !== this.props.channel?.id) {
      this.startingMessagesStarted = false;
    }

    if (
      this.props.startingMessages?.length &&
      !this.startingMessagesStarted &&
      !isAwaitingLanguageSelection(this.props.channel)
    ) {
      const { startingMessages, client } = this.props;
      this.startingMessagesStarted = true;
      this.setState({ typing: true });
      setTimeout(() => {
        this.addGraduallyMessages(client, this.props.channel.id, 0, startingMessages);
      }, CHATTING_ANIMATION_LENGTH);
    }
  }

  handleRangeChoosed = (message: string, index: number) => {
    this.setState({ message, values: [index] });
    this.makeBounce();
  };

  setLastKey = (key: string | undefined) => {
    this.setState({ lastKey: key });
  };

  handleSelectPerson = async (person: organizationRespondent) => {
    await this.setState((oldState) => ({
      selectedPersons: [...oldState.selectedPersons, person],
    }));
    this.setState({ inputInitValue: '' });
  };

  handleRemoveRespondent = (respondent: organizationRespondent) => {
    this.setState((oldState) => ({
      selectedPersons: oldState.selectedPersons.filter((res) => res.id !== respondent.id),
    }));
  };

  makeBounce = (makeBounce?: boolean) => {
    if (makeBounce != null) {
      this.setState({ bounce: false });
    } else {
      this.setState({ bounce: true });
      setTimeout(() => this.setState({ bounce: false }), 2800);
    }
  };

  prepareBounce = () => {
    clearTimeout(this.bounce);
    this.bounce = setTimeout(this.makeBounce, 3000);
  };

  handleChangeLastMessage = (message: any) => {
    this.setState({ lastMessage: message });
  };

  changeInputInitValue = (type: string, message: string, values?: number[]) => {
    if (
      type === 'FreeTextAnswer' ||
      type === 'SmsContactQuestionDefinition' ||
      type === 'EmailContactQuestionDefinition'
    ) {
      this.setState({ inputInitValue: message });
    } else {
      this.setState(values ? { inputInitValue: '', values } : { inputInitValue: '', values: [] });
    }
  };

  handleSetMessage = (message: string) => {
    this.setState({ message });
  };

  // Scale state management
  initScaleState = (statementsLength: number) => {
    this.setState((prev) => {
      if (prev.scaleSelections.length === statementsLength && prev.scaleComments.length === statementsLength) {
        return prev;
      }
      return {
        ...prev,
        scaleSelections: Array(statementsLength).fill(null),
        scaleComments: Array(statementsLength).fill(null),
      } as IProviderState;
    });
  };

  setScaleSelection = (statementIndex: number, choiceIndex: number) => {
    this.setState((prev) => {
      const next = [...prev.scaleSelections];
      next[statementIndex] = choiceIndex;
      return { ...prev, scaleSelections: next } as IProviderState;
    });
  };

  setScaleComment = (statementIndex: number, comment: string | null) => {
    this.setState((prev) => {
      const next = [...prev.scaleComments];
      next[statementIndex] = comment;
      return { ...prev, scaleComments: next } as IProviderState;
    });
  };

  prefillScaleState = (selections: (number | null)[], comments: (string | null)[]) => {
    this.setState((prev) => ({
      ...prev,
      scaleSelections: selections,
      scaleComments: comments,
      prefilledScaleSelections: selections,
    }));
  };

  writeMessageToStore = (store: any, message: any, channelId: string) => {
    try {
      const queryMessage = clone(
        store.readQuery({
          query: messagesQuery,
          variables: { channelId },
        }),
      );
      let someMessagesWereWritten = false;
      const messagesToWrite = Array.isArray(message) ? message : [message];
      if (!queryMessage?.messages) {
        const toWrite = messagesToWrite.filter(Boolean);
        if (toWrite.length) {
          store.writeQuery({ query: messagesQuery, data: { messages: toWrite }, variables: { channelId } });
        }
        return;
      }
      messagesToWrite.forEach((messageToWrite) => {
        if (
          messageToWrite &&
          !queryMessage.messages.find((msg: { id: string }) => msg.id === messageToWrite.id) &&
          Number(messageToWrite.id) >= 0
        ) {
          queryMessage.messages.push({ ...messageToWrite, value: messageToWrite.value || this.state.values });
          someMessagesWereWritten = true;
        }
      });
      if (someMessagesWereWritten) {
        store.writeQuery({
          query: messagesQuery,
          data: queryMessage,
          variables: { channelId },
        });
      }
    } catch (e) {
      console.error('Error writing message to store', e);
      const dataQuery = {
        messages: [message],
      };
      store.writeQuery({
        query: messagesQuery,
        data: dataQuery,
        variables: { channelId },
      });
    }
  };

  addGraduallyMessages = (
    client: ApolloClient<any>,
    channelId: string,
    index: number,
    startingMessages: NonNullable<GetMessagesQuery['messages']>,
  ) => {
    this.writeMessageToStore(client, startingMessages[index], channelId);
    this.setState({ typing: false });
    if (index + 1 < startingMessages.length) {
      setTimeout(() => {
        this.setState({
          typing: true,
          typingStart: Date.now(),
        });
        setTimeout(() => {
          this.addGraduallyMessages(client, channelId, index + 1, startingMessages);
        }, CHATTING_ANIMATION_LENGTH);
      }, 100);
    } else {
      setTimeout(() => {
        this.setState({ hideControls: false });
      }, CHATTING_ANIMATION_LENGTH);
    }
  };

  handleSendMessage = async (message: string | null, index?: number, notSendValues?: number[], id?: string) => {
    const { channel, client } = this.props;
    const { values, notDeliveredMessages, lastMessage, selectedPersons } = this.state;
    this.setState({
      inputInitValue: '',
      typing: true,
      typingStart: Date.now(),
      hideControls: true,
    });
    clearTimeout(this.bounce);
    const optional = path(['definition', 'optional'], lastMessage);

    // send proper mutation based on previous message
    const isPersonMultiselect = this.hasTypename(definitionTypes[QuestionType.PersonMultiselect]);
    const isTextMessage = this.hasTypename(definitionTypes[QuestionType.Freetext]);
    const isSmsContactMessage = this.hasTypename(definitionTypes[QuestionType.SmsContact]);
    const isEmailContactMessage = this.hasTypename(definitionTypes[QuestionType.EmailContact]);

    if (!optional && isTextMessage && message === '' && this.state.message === '') {
      return false;
    }

    let newArr: any[] = [];
    if (index) {
      newArr = [
        ...notDeliveredMessages.slice(0, index - 1),
        ...notDeliveredMessages.slice(index, notDeliveredMessages.length),
      ];
      this.setState({ notDeliveredMessages: newArr });
    }

    const makeUpdate =
      (mutation: string) =>
      (store: any, { data }: { data: any }) => {
        if (!data || data[mutation] == null) {
          this.setState({ typing: false, hideControls: false });
          return;
        }
        const rawAnswer = data[mutation];

        const answersArray = Array.isArray(rawAnswer) ? rawAnswer : [rawAnswer];
        this.writeMessageToStore(store, answersArray, channel.id);

        let nextResponse: any[] | null = null;
        let latestRemainingQuestions: number | null | undefined = null;
        for (let i = answersArray.length - 1; i >= 0; i -= 1) {
          const item = answersArray[i];
          if (Number.isInteger(item?.remainingQuestions)) {
            latestRemainingQuestions = item.remainingQuestions;
          }
          if (item?.response && item.response.length) {
            nextResponse = item.response;
            break;
          }
        }

        if (Number.isInteger(latestRemainingQuestions as any)) {
          this.setState({ remainingQuestions: latestRemainingQuestions as number });
        }

        if (nextResponse && nextResponse.length) {
          const diff = Date.now() - this.state.typingStart;
          setTimeout(
            () => {
              this.addGraduallyMessages(store, this.props.channel.id, 0, nextResponse as any);
            },
            diff > CHATTING_ANIMATION_LENGTH ? 0 : CHATTING_ANIMATION_LENGTH - diff,
          );
        } else {
          this.setState({ typing: false, hideControls: false });
        }
      };

    const variables = {
      channelId: channel.id,
      previousQuestionId: lastMessage?.id,
      ...(isTextMessage || isSmsContactMessage || isEmailContactMessage
        ? { text: message }
        : {
            values:
              notSendValues ||
              (isPersonMultiselect ? selectedPersons.map((i) => parseInt(i.id.toString(), 10)) : values),
            message: message || '',
          }),
    };

    const callSendMessage = () => {
      const mutation =
        isTextMessage || isSmsContactMessage || isEmailContactMessage ? 'sendTextAnswer' : 'sendSelectAnswer';
      const mutationFunction: any = this[mutation];
      return mutationFunction.call(this, message || this.state.message, {
        variables,
        update: makeUpdate(mutation),
      });
    };
    this.setState({
      message: '',
      values: [],
      index: 0,
      selectedPersons: [],
      isSending: true,
    });
    callSendMessage()
      .then(() => {
        this.setState({ notDeliveredMessages: [], err: null, isSending: false });
      })
      .catch((err: any) => {
        client.refetchQueries({
          include: [{ query: messagesQuery, variables: { channelId: channel.id } }],
        });
        this.setState({ err, typing: false, isSending: false, hideControls: false });
        this.addNotDeriveMess(
          newArr,
          notDeliveredMessages,
          message || this.state.message,
          notSendValues || (isPersonMultiselect ? selectedPersons.map((i) => i.id) : values),
          index,
          id,
        );
      });
    return true;
  };

  addNotDeriveMess = (
    newArr: any[],
    notDeliveredMessages: any[],
    message: string,
    values?: (number | string)[],
    index?: number,
    id?: string,
  ) => {
    const {
      channel: { anonymityLevel },
    } = this.props;
    const newMessage = {
      id: id != null ? id : -notDeliveredMessages.length.toString(),
      text: message,
      values,
      createdAt: new Date().toISOString(),
      createdBy: 'RESPONDENT',
      __typename: 'Answer',
      anonymityLevel,
    };
    if (index === undefined) {
      this.setState({
        notDeliveredMessages: [...this.state.notDeliveredMessages, newMessage],
      });
    } else {
      this.setState({ notDeliveredMessages: [...newArr, newMessage] });
    }
  };

  sendTextAnswer = (message: string, options: any) => {
    const {
      sendTextAnswer,
      channel: { anonymityLevel },
    } = this.props;

    return sendTextAnswer({
      optimisticResponse: {
        sendTextAnswer: {
          id: '-1',
          text: this.hasTypename(definitionTypes[QuestionType.SmsContact])
            ? message
                .replace(/ /g, '')
                .split(/(?=(?:...)*$)/)
                .join(' ')
                .replace('+ ', '+')
            : message,
          createdAt: new Date().toISOString(),
          createdBy: 'RESPONDENT',
          __typename: 'FreeTextAnswer',
          anonymityLevel,
          remainingQuestions: this.state.remainingQuestions ?? this.props.channel?.remainingQuestions ?? null,
          response: [],
          isLast: false,
          question: {
            __typename: 'Question',
            definition: path(['definition'], this.state.lastMessage) ?? null,
          },
        },
      },
      ...options,
    });
  };

  sendSelectAnswer = (message: string, options: any) => {
    const {
      sendSelectAnswer,
      channel: { anonymityLevel },
    } = this.props;

    const choices = path<any>(['definition', 'choices'], this.state.lastMessage) ?? undefined;

    return sendSelectAnswer({
      optimisticResponse: {
        sendSelectAnswer: {
          id: '-1',
          text: message,
          createdAt: new Date().toISOString(),
          createdBy: 'RESPONDENT',
          __typename: 'SelectAnswer',
          anonymityLevel,
          response: [],
          isLast: false,
          value: options.variables.values,
          // Provide remainingQuestions to satisfy selection set
          remainingQuestions: this.state.remainingQuestions ?? this.props.channel?.remainingQuestions ?? null,
          // Provide minimal question shape expected by UI and query
          question: {
            __typename: 'Question',
            definition: {
              selectedRange: path<number[]>(['definition', 'selectedRange'], this.state.lastMessage) || null,
              choices,
              text: path<string>(['definition', 'text'], this.state.lastMessage) ?? null,
              __typename: 'SelectQuestionDefinition',
            },
          },
        },
      },
      ...options,
    });
  };

  handleChangeMessage = (message: string) => {
    this.setState({
      message,
    });
  };

  hasTypename = (type: string) => {
    const { lastMessage } = this.state;
    const typeName = path(['definition', '__typename'], lastMessage);
    return typeName && typeName === type;
  };

  handleAddChoice = (message: string, index: number) => {
    const { lastMessage } = this.state;
    const selectedRange = path<number[]>(['definition', 'selectedRange'], lastMessage);
    if (selectedRange && selectedRange[1] === 1) {
      this.setState({ values: [index] }, () => this.handleSendMessage(message, index));
    } else {
      this.setState((prevState) => ({
        message: prevState.message !== '' ? `${prevState.message}, ${message}` : message,
        values: [...prevState.values, index],
      }));
      this.prepareBounce();
    }
  };

  handleRemoveChoice = (index: number) => {
    this.setState((prevState) => ({
      values: remove(index, 1, prevState.values),
    }));
    this.prepareBounce();
  };

  setClearInputFunction = (clearInput: () => void) => {
    this.clearInputFunction = clearInput;
  };

  clearInput = () => {
    this.clearInputFunction?.();
  };

  setRemainingQuestions = (remainingQuestions: number) => {
    this.setState({ remainingQuestions });
  };

  submitScaleAnswers = async (): Promise<boolean> => {
    const { channel, client } = this.props;
    const { lastMessage, scaleSelections, scaleComments } = this.state;

    const answers: IScaleAnswerInputItem[] = [];
    for (let idx = 0; idx < scaleSelections.length; idx += 1) {
      const choice = scaleSelections[idx];
      if (choice != null) {
        answers.push({ statementIndex: idx, value: choice, comment: scaleComments[idx] ?? null });
      }
    }

    const statementsLength = (path<string[]>(['definition', 'statements'], lastMessage) || []).length;
    const allAnswered = answers.length === statementsLength;
    if (!allAnswered) {
      return false;
    }

    this.setState({
      typing: true,
      typingStart: Date.now(),
      hideControls: true,
      isSending: true,
    });

    const makeUpdate =
      (mutation: string) =>
      (store: any, { data }: { data: any }) => {
        if (!data || data[mutation] == null) {
          this.setState({ typing: false, hideControls: false });
          return;
        }
        const rawAnswer = data[mutation];

        const answersArray = Array.isArray(rawAnswer) ? rawAnswer : [rawAnswer];

        this.writeMessageToStore(store, answersArray, channel.id);

        let nextResponse: any[] | null = null;
        let latestRemainingQuestions: number | null | undefined = null;
        for (let i = answersArray.length - 1; i >= 0; i -= 1) {
          const item = answersArray[i];
          if (Number.isInteger(item?.remainingQuestions)) {
            latestRemainingQuestions = item.remainingQuestions;
          }
          if (item?.response && item.response.length) {
            nextResponse = item.response;
            break;
          }
        }

        if (Number.isInteger(latestRemainingQuestions as any)) {
          this.setState({ remainingQuestions: latestRemainingQuestions as number });
        }

        if (nextResponse && nextResponse.length) {
          const diff = Date.now() - this.state.typingStart;
          setTimeout(
            () => {
              this.addGraduallyMessages(store, this.props.channel.id, 0, nextResponse as any);
            },
            diff > CHATTING_ANIMATION_LENGTH ? 0 : CHATTING_ANIMATION_LENGTH - diff,
          );
        } else {
          this.setState({ typing: false, hideControls: false });
        }
      };

    const variables: ISendScaleMutationInput = {
      channelId: channel.id,
      previousQuestionId: this.state.lastMessage?.id,
      answers,
    };

    try {
      const {
        sendScaleAnswer,
        channel: { anonymityLevel },
      } = this.props;

      const choices = path<any>(['definition', 'choices'], this.state.lastMessage) ?? undefined;
      const statements = path<any>(['definition', 'statements'], this.state.lastMessage) ?? undefined;

      await sendScaleAnswer({
        optimisticResponse: {
          sendScaleAnswer: variables.answers.map((answer, i) => ({
            id: `-${i + 1}`,
            text: '',
            createdAt: new Date().toISOString(),
            createdBy: 'RESPONDENT',
            __typename: 'ScaleAnswer',
            anonymityLevel,
            isLast: false,
            remainingQuestions: null,
            statementIndex: answer.statementIndex,
            comment: answer.comment,
            value: answer.value,
            response: [],
            question: {
              __typename: 'Question',
              definition: {
                choices,
                statements,
                text: path<string>(['definition', 'text'], this.state.lastMessage) ?? null,
                randomizeOrder: path<boolean>(['definition', 'randomizeOrder'], this.state.lastMessage) ?? null,
                isRankingScale: path<boolean>(['definition', 'isRankingScale'], this.state.lastMessage) ?? null,
                showEscapeOption: path<boolean>(['definition', 'showEscapeOption'], this.state.lastMessage) ?? null,
                id: path<string>(['definition', 'id'], this.state.lastMessage) ?? null,
                __typename: 'ScaleQuestionDefinition',
              },
            },
          })) as any,
        },
        variables,
        update: makeUpdate('sendScaleAnswer') as any,
      });
      this.setState({
        scaleSelections: [],
        scaleComments: [],
        prefilledScaleSelections: null,
        err: null,
        isSending: false,
      });
      return true;
    } catch (err: any) {
      console.error('Error sending scale answers');
      console.error(err);
      client.refetchQueries({
        include: [{ query: messagesQuery, variables: { channelId: channel.id } }],
      });
      this.setState({ err, typing: false, isSending: false, hideControls: false });
      return false;
    }
  };

  render() {
    return (
      <InputContext.Provider
        value={{
          state: this.state,
          organizationId: this.props.channel?.respondent?.organizationId,
          changeMessage: this.handleChangeMessage,
          sendMessage: this.handleSendMessage,
          removeChoice: this.handleRemoveChoice,
          addChoice: this.handleAddChoice,
          rangeChoosed: this.handleRangeChoosed,
          changeLastMessage: this.handleChangeLastMessage,
          makeBounce: this.makeBounce,
          editInputValue: this.changeInputInitValue,
          handleSetMessage: this.handleSetMessage,
          handleSelectPerson: this.handleSelectPerson,
          handleRemoveRespondent: this.handleRemoveRespondent,
          setLastKey: this.setLastKey,
          setClearInputFunction: this.setClearInputFunction,
          clearInput: this.clearInput,
          setRemainingQuestions: this.setRemainingQuestions,
          setScaleSelection: this.setScaleSelection,
          setScaleComment: this.setScaleComment,
          initScaleState: this.initScaleState,
          prefillScaleState: this.prefillScaleState,
          submitScaleAnswers: this.submitScaleAnswers,
        }}
      >
        {this.props.children}
      </InputContext.Provider>
    );
  }
}

const WithMessagesOperations = compose(
  graphql<IProps>(sendTextAnswerMutation, {
    name: 'sendTextAnswer',
  }),
  graphql<IProps>(sendSelectAnswerMutation, {
    name: 'sendSelectAnswer',
  }),
  graphql<IProps>(sendScaleAnswerMutation, {
    name: 'sendScaleAnswer',
  }),
);

export const InputProvider = WithMessagesOperations(InputProviderComponent);
export const InputConsumer = InputContext.Consumer;
